/**
 * 预测结果区「传承回填」邀请 ——「帮助别人，也成就自己」
 *
 * 放在五校预测下方，把「回填」从冷冰冰的数据请求，升华为一份届届相传的善意：
 *  - 你现在用的预测，是往届家长真实回填校准出来的
 *  - 录取后回填你的结果，让明年的孩子更准一分
 *
 * 阶段自适应：
 *  - 录取后（after_admission）：可直接展开真实回填表单（FeedbackSection）
 *  - 出分 / 填报期：先种下承诺，引导关注服务号、录取后提醒回来
 */
import { useState } from 'react'
import type { CalcResult } from '@/domain/types'
import type { StageInfo } from '@/domain/stage'
import { FeedbackSection } from './FeedbackSection'
import { track } from '@/lib/track'

export function BackfillPledge({
  result,
  stageInfo
}: {
  result: CalcResult
  stageInfo: StageInfo
}) {
  const [open, setOpen] = useState(false)
  const [reminded, setReminded] = useState(false)

  // 资格线下且无段位时无意义
  if (!result.position.band && !result.position.inTop300) return null

  const isAfter = stageInfo.stage === 'after_admission'

  return (
    <>
      <section className="card bg-gradient-to-br from-rose-50 to-amber-50 border border-amber-100">
        <div className="flex items-start gap-3">
          <div className="text-3xl leading-none" aria-hidden="true">🤝</div>
          <div className="flex-1">
            <h3 className="text-h3 text-ink-900 mb-1.5">帮助别人，也成就自己</h3>
            <p className="text-ink-700 text-sm leading-relaxed">
              你现在看到的每一个录取概率，都是往届家长在录取后
              <span className="font-medium text-ink-900">真实回填分数与结果</span>
              、一点点校准出来的。等你家孩子今年尘埃落定，也欢迎回来填一笔——
              你这一次回填，会让明年学弟学妹的预测更准一分。善意一届届传下去，
              也让这份工具因你而更可信。
            </p>

            {isAfter ? (
              <>
                {/* 激励文案待明天确定，暂时隐藏（原文：作为答谢，回填后将赠 4 张共 200 元的卓越教育学长答谢券（电子券，进店核销）。） */}
                {!open && (
                  <button
                    onClick={() => {
                      setOpen(true)
                      track('drill_click', { target: 'open_backfill', from: 'pledge' })
                    }}
                    className="btn-primary mt-3 px-5 py-2 text-sm"
                  >
                    立即回填录取结果 →
                  </button>
                )}
              </>
            ) : reminded ? (
              <p className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 leading-relaxed">
                已为你记下这份约定 💛 录取结果一般 7 月中下旬公布，
                关注服务号「卓越方法星球」，到时我们会提醒你回来回填。
              </p>
            ) : (
              <button
                onClick={() => {
                  setReminded(true)
                  track('drill_click', { target: 'backfill_remind', from: 'pledge' })
                }}
                className="btn-ghost mt-3 px-5 py-2 text-sm"
              >
                和我约定：录取后回来回填
              </button>
            )}
          </div>
        </div>
      </section>

      {open && <FeedbackSection result={result} />}
    </>
  )
}
